import { createRouter, createWebHistory, RouterView, RouterLink } from 'vue-router';

import store from './store/store';
import { config } from '@/ga/user';

import LayoutMain from './components/layouts/main/Main.vue';
import FullPage from './components/layouts/full-page/FullPage.vue';
import SlideReport from './views/SlideReport';
import Dashboard from './views/Dashboard';
import Trend from './views/Trend';
import CampaignReport from './views/CampaignReport';
import AmcRepeat from './views/amcReports/Repeat.vue';
import AmcArpu from './views/amcReports/Arpu.vue';
import AmcRfm from './views/amcReports/Rfm.vue';
import AmcAdCampaign from './views/amcReports/AdCampaign.vue';
import AmcAdKeyword from './views/amcReports/AdKeyword.vue';
import AmcSegment from './views/amcReports/Segment.vue';
import AmcEcUserAnalysis from './views/amcReports/EcUserAnalysis.vue';
import AmcSetting from './views/amcReports/Setting.vue';
import AmcAffinity from './views/amcReports/Affinity.vue';
import AmcAudience from './views/amcReports/Audience.vue';
import AmcBrandReports from './views/amcReports/BrandReports.vue';
import Oauth from './views/Oauth';
import ReOauth from './views/ReOauth';
import Analytics from './views/Analytics';
import Measure from './views/measure/Measure';
import SalesAsin from './views/salesReports/Asin';
import SalesAsinByDay from './views/salesReports/AsinByDay';
import SalesAsinByMonth from './views/salesReports/AsinByMonth';
import SalesByDay from './views/salesReports/ByDay';
import SalesByMonth from './views/salesReports/ByMonth';
import Asin from './views/reports/Asin';
import AsinByDay from './views/reports/AsinByDay';
import AsinByMonth from './views/reports/AsinByMonth';
import ByDay from './views/reports/ByDay';
import ByMonth from './views/reports/ByMonth';
import Product from './views/Product';
import Login from './views/pages/Login';
import Register from '@/user/views/pages/Register';
import Forget from '@/user/views/pages/password/Forget';
import Reset from '@/user/views/pages/password/Reset';
import Maintenance from './views/pages/Maintenance';
import NotFound from './views/pages/Error404';
import MonitoringKeyword from '@/user/views/monitorings/Keyword';
import MonitoringAsin from '@/user/views/monitorings/Asin';
import KeywordReports from '@/user/views/KeywordReports';
import ReviewRequest from '@/user/views/ReviewRequest';
import News from '@/user/views/News';
import AccountManagement from '@/user/views/settings/AccountManagement';
import UserManagement from '@/user/views/settings/UserManagement';
import UserInvite from '@/user/views/pages/UserInvite';
import ReviewAnalysis from '@/user/views/ReviewAnalysis';
import AdBid from '@/user/views/AdBid';

const ifNotAuthenticated = (to, from, next) => {
  if (!store.getters['auth/isAuthenticated']) {
    next();
    return;
  }
  next('/');
};

const ifAuthenticated = (to, from, next) => {
  if (!store.getters['auth/isAuthenticated']) {
    next('/login');
  } else if (to.path !== '/oauth' && !store.getters['account/isActiveAccountConnected']) {
    next('/oauth');
  } else if (to.path !== '/reoauth' && store.getters['account/isActiveAccountConnected'] && store.getters['account/isActiveAccountNeedReauth']) {
    next('/reoauth');
  } else {
    next();
  }
};

const routes = [
  {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    path: '/',
    component: LayoutMain,
    children: [
      // =============================================================================
      // Theme Routes
      // =============================================================================
      {
        path: '/slide-report',
        name: 'slide-report',
        component: SlideReport,
        meta: {
          requiresAuth: true,
          pageTitle: 'マンスリーレポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/measure',
        name: 'measure',
        component: Measure,
        meta: {
          requiresAuth: true,
          pageTitle: '施策レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/',
        name: 'dashboard',
        component: Dashboard,
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/trend',
        name: 'trend',
        component: Trend,
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/campaign-report',
        name: 'campaign-report',
        component: CampaignReport,
        meta: {
          requiresAuth: true,
          pageTitle: 'キャンペーンレポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/repeat',
        name: 'amc-reports-repeat',
        component: AmcRepeat,
        meta: {
          requiresAuth: true,
          pageTitle: 'リピート分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/arpu',
        name: 'amc-reports-arpu',
        component: AmcArpu,
        meta: {
          requiresAuth: true,
          pageTitle: 'ARPU分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/rfm',
        name: 'amc-reports-rfm',
        component: AmcRfm,
        meta: {
          requiresAuth: true,
          pageTitle: 'RFM分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/ad-campaign',
        name: 'amc-reports-ad-campaign',
        component: AmcAdCampaign,
        meta: {
          requiresAuth: true,
          pageTitle: '広告キャンペーン分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/ad-keyword',
        name: 'amc-reports-ad-keyword',
        component: AmcAdKeyword,
        meta: {
          requiresAuth: true,
          pageTitle: '広告キーワード分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/segment',
        name: 'amc-reports-segment',
        component: AmcSegment,
        meta: {
          requiresAuth: true,
          pageTitle: '注文率分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/affinity',
        name: 'amc-reports-affinity',
        component: AmcAffinity,
        meta: {
          requiresAuth: true,
          pageTitle: 'アフィニティ分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/brand-reports',
        name: 'amc-brand-reports',
        component: AmcBrandReports,
        meta: {
          requiresAuth: true,
          pageTitle: 'AMCブランドレポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/ec-user-analysis',
        name: 'amc-reports-ec-user-analysis',
        component: AmcEcUserAnalysis,
        meta: {
          requiresAuth: true,
          pageTitle: '自社ECユーザー分析',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/setting',
        name: 'amc-reports-setting',
        component: AmcSetting,
        meta: {
          requiresAuth: true,
          pageTitle: 'AMCレポート設定',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/amc-reports/audience',
        name: 'amc-reports-audience',
        component: AmcAudience,
        meta: {
          requiresAuth: true,
          pageTitle: 'オーディエンス作成',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/analytics',
        name: 'analytics',
        component: Analytics,
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/products',
        name: 'products',
        component: Product,
        meta: {
          requiresAuth: true,
          pageTitle: '商品',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/sales-reports/asin',
        name: 'sales-reports-asin',
        component: SalesAsin,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN別', active: true },
          ],
          pageTitle: '売上レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/sales-reports/asinbyday',
        name: 'sales-reports-asinbyday',
        component: SalesAsinByDay,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN×日別', active: true },
          ],
          pageTitle: '売上レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/sales-reports/asinbymonth',
        name: 'sales-reports-asinbymonth',
        component: SalesAsinByMonth,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN×月別', active: true },
          ],
          pageTitle: '売上レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/sales-reports/byday',
        name: 'sales-reports-byday',
        component: SalesByDay,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: '日別', active: true },
          ],
          pageTitle: '売上レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/sales-reports/bymonth',
        name: 'sales-reports-bymonth',
        component: SalesByMonth,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: '月別', active: true },
          ],
          pageTitle: '売上レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reports/asin',
        name: 'reports-asin',
        component: Asin,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN別', active: true },
          ],
          pageTitle: '利益レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reports/asinbyday',
        name: 'reports-asinbyday',
        component: AsinByDay,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN×日別', active: true },
          ],
          pageTitle: '利益レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reports/asinbymonth',
        name: 'reports-asinbymonth',
        component: AsinByMonth,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN×月別', active: true },
          ],
          pageTitle: '利益レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reports/byday',
        name: 'reports-byday',
        component: ByDay,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: '日別', active: true },
          ],
          pageTitle: '利益レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reports/bymonth',
        name: 'reports-bymonth',
        component: ByMonth,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: '月別', active: true },
          ],
          pageTitle: '利益レポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/keyword-reports',
        name: 'keyword-reports',
        component: KeywordReports,
        meta: {
          requiresAuth: true,
          pageTitle: '広告キーワードレポート',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/ad-bid',
        name: 'ad-bid',
        component: AdBid,
        meta: {
          requiresAuth: true,
          pageTitle: '広告自動入札',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/review-request',
        name: 'review-request',
        component: ReviewRequest,
        meta: {
          requiresAuth: true,
          pageTitle: 'レビューリクエスト',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/review-analysis',
        name: 'review-analysis',
        component: ReviewAnalysis,
        meta: {
          requiresAuth: true,
          pageTitle: 'レビュー分析',
          subtitle: 'Powered by ChatGPT API',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/monitoring/keyword',
        name: 'monitoring-keyword',
        component: MonitoringKeyword,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'キーワード', active: true },
          ],
          pageTitle: 'モニタリング設定',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/monitoring/asin',
        name: 'monitoring-asin',
        component: MonitoringAsin,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ASIN', active: true },
          ],
          pageTitle: 'モニタリング設定',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/oauth',
        name: 'oauth',
        component: Oauth,
        meta: {
          requiresAuth: true,
          pageTitle: 'アカウント接続',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/reoauth',
        name: 'reoauth',
        component: ReOauth,
        meta: {
          requiresAuth: true,
          pageTitle: 'アカウント接続の更新',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/resource',
        name: 'resource',
        beforeEnter: async (to, from, next) => {
          await store.dispatch('resource/redirectResource');
          next(false);
        },
      },
      {
        path: '/community',
        name: 'community',
      },
      {
        path: '/news',
        name: 'news',
        component: News,
        meta: {
          requiresAuth: true,
          pageTitle: 'お知らせ',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/settings/account-management',
        name: 'settings-account-management',
        component: AccountManagement,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'アカウント管理', active: true },
          ],
          pageTitle: '設定',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/settings/user-management',
        name: 'settings-user-management',
        component: UserManagement,
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'ユーザー管理', active: true },
          ],
          pageTitle: '設定',
        },
        beforeEnter: ifAuthenticated,
      },
      {
        path: '/settings/cancellation',
        name: 'settings-cancellation',
      },
    ],
  },
  // =============================================================================
  // FULL PAGE LAYOUTS
  // =============================================================================
  {
    path: '/',
    component: FullPage,
    children: [
      // =============================================================================
      // PAGES
      // =============================================================================
      {
        path: '/login',
        name: 'page-login',
        component: Login,
        beforeEnter: ifNotAuthenticated,
      },
      {
        path: '/register',
        name: 'page-register',
        component: Register,
        beforeEnter: ifNotAuthenticated,
      },
      {
        path: '/user/invite/:token',
        name: 'page-user-invite',
        component: UserInvite,
        beforeEnter: ifNotAuthenticated,
      },
      {
        path: '/password/forget',
        name: 'page-password-forget',
        component: Forget,
        beforeEnter: ifNotAuthenticated,
      },
      {
        path: '/password/reset/:token',
        name: 'page-password-reset',
        component: Reset,
        beforeEnter: ifNotAuthenticated,
      },
      {
        path: '/maintenance',
        name: 'maintenance',
        component: Maintenance,
      },
      {
        path: '/pages/error-404',
        name: 'page-error-404',
        component: NotFound,
      },
    ],
  },
  // Redirect to 404 page, if no match found
  {
    path: '/:catchAll(.*)',
    redirect: '/pages/error-404',
  },
];

RouterView.compatConfig = { MODE: 3 };
RouterLink.compatConfig = { MODE: 3 };

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
  routes,
});

router.afterEach((to) => {
  config({
    page_title: to.name,
    page_path: to.path,
    page_location: window.location.href,
  });
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

export default router;
